(function () {
  const SELECTOR = '[data-noxwell-three-video]';
  const MOBILE_QUERY = '(max-width: 749px)';
  const MANUAL_PAUSE_MS = 12000;

  class NoxwellThreeVideoBanner {
    constructor(section) {
      this.section = section;
      this.scenes = Array.from(section.querySelectorAll('[data-video-scene]'));
      this.tabs = Array.from(section.querySelectorAll('[data-video-tab]'));
      this.descriptions = Array.from(section.querySelectorAll('[data-video-description]'));
      this.activeIndex = 0;
      this.interval = Number(section.dataset.interval) || 8000;
      this.autoplay = section.dataset.autoplay === 'true';
      this.timer = null;
      this.resumeTimer = null;
      this.inView = true;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mobileMedia = window.matchMedia(MOBILE_QUERY);

      this.bind();
      this.loadScene(0);

      if (this.reducedMotion) {
        this.pauseAllVideos();
        return;
      }

      this.playActiveVideo();
      this.startTimer();
    }

    bind() {
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.manualActivate(index));
        tab.addEventListener('focus', () => this.manualActivate(index));
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopTimer();
          this.pauseAllVideos();
        } else {
          this.playActiveVideo();
          this.startTimer();
        }
      });

      this.mobileMedia.addEventListener('change', () => {
        this.reloadActiveScene();
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            this.inView = Boolean(entry && entry.isIntersecting);
            if (this.inView) {
              this.playActiveVideo();
              this.startTimer();
            } else {
              this.stopTimer();
              this.pauseAllVideos();
            }
          },
          { threshold: 0.2 }
        );
        this.observer.observe(this.section);
      }
    }

    getVideoUrl(scene) {
      const desktopUrl = scene.dataset.desktopVideo || '';
      const mobileUrl = scene.dataset.mobileVideo || '';
      return this.mobileMedia.matches && mobileUrl ? mobileUrl : desktopUrl;
    }

    loadScene(index) {
      const scene = this.scenes[index];
      if (!scene) return;

      const video = scene.querySelector('video');
      const url = this.getVideoUrl(scene);
      if (!video || !url || video.dataset.loadedUrl === url) return;

      video.muted = true;
      video.playsInline = true;
      video.loop = true;
      video.preload = index === this.activeIndex ? 'metadata' : 'none';
      video.innerHTML = '';

      const source = document.createElement('source');
      source.src = url;
      source.type = 'video/mp4';
      video.appendChild(source);
      video.dataset.loadedUrl = url;
      video.load();
    }

    reloadActiveScene() {
      const scene = this.scenes[this.activeIndex];
      if (!scene) return;
      const video = scene.querySelector('video');
      const url = this.getVideoUrl(scene);
      if (video && video.dataset.loadedUrl !== url) {
        video.dataset.loadedUrl = '';
        this.loadScene(this.activeIndex);
      }
      this.playActiveVideo();
    }

    activate(index) {
      if (index === this.activeIndex || !this.scenes[index]) return;

      this.activeIndex = index;
      this.loadScene(index);

      this.scenes.forEach((scene, sceneIndex) => {
        scene.classList.toggle('is-active', sceneIndex === index);
      });

      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
      });

      this.descriptions.forEach((description, descriptionIndex) => {
        description.classList.toggle('is-active', descriptionIndex === index);
      });

      this.pauseAllVideos();
      this.playActiveVideo();
    }

    activateNext() {
      this.activate((this.activeIndex + 1) % this.scenes.length);
      this.startTimer();
    }

    manualActivate(index) {
      this.activate(index);
      this.stopTimer();
      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => this.startTimer(), MANUAL_PAUSE_MS);
    }

    playActiveVideo() {
      if (this.reducedMotion || !this.inView || document.hidden) return;

      const scene = this.scenes[this.activeIndex];
      const video = scene && scene.querySelector('video');
      if (!video || !video.dataset.loadedUrl) return;

      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    pauseAllVideos() {
      this.scenes.forEach((scene) => {
        const video = scene.querySelector('video');
        if (video) video.pause();
      });
    }

    startTimer() {
      if (!this.autoplay || this.reducedMotion || !this.inView || document.hidden || this.scenes.length < 2) return;
      this.stopTimer();
      this.timer = window.setTimeout(() => this.activateNext(), this.interval);
    }

    stopTimer() {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  function init() {
    document.querySelectorAll(SELECTOR).forEach((section) => {
      if (!section.dataset.noxwellThreeVideoReady) {
        section.dataset.noxwellThreeVideoReady = 'true';
        new NoxwellThreeVideoBanner(section);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
